/* import __COLOCATED_TEMPLATE__ from './company-profile-members-list-item.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import clsx from 'clsx';

import { COMPANY_PROFILE__USER_TEAM_MEMBERSHIP_INCLUDES } from 'sh-www/constants/routes/company-profile';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';

const COMPANY_USER_ROLES = {
  ADMIN: 'admin',
  EDITOR: 'editor',
}

const CLASS_NAME = 'SH-c-company-profile-members-list-item';

class CompanyMembers extends Component {
  @service('state-managers/application') applicationStateManager;
  @service media;
  @service router;
  @service session;
  @service store;
  @service('commands/user-commands') userCommandService;
  @service('commands/company-team-membership-commands') companyTeamMembershipCommandService;

  get classNames() {
    return clsx(
      CLASS_NAME,
      this.args.class,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  get menuTeamOptions() {
    const { company, teams, user, userTeams } = this.args;
    const menuOptions = [];

    const allTeamIds = teams.map(({ id }) => id);
    const usersTeamIds = userTeams.map(({ id }) => id);

    if (allTeamIds.length - usersTeamIds.length > 1) {
      menuOptions.push({
        title: 'All Teams',
        onClick: () => this.addUserToAllTeams.perform(user, company),
      });
    }

    teams
      .filter(({ id }) => !usersTeamIds.includes(id))
      .forEach((team) => {
        menuOptions.push({
          title: team.name,
          onClick: () => this.addCompanyTeamMembership.perform(team, user, company),
        });
    });

    return menuOptions;
  }

  get menuAdditionalOptions() {
    const { currentUser } = this.session;
    const { user } = this.args;
    const menuOptions = [];

    const currentUserIsSiteAdmin = currentUser.isAdmin;
    const currentUserIsCompanyAdmin = currentUser.isCompanyAdmin;

    if (currentUserIsSiteAdmin) {
      menuOptions.push({
        title: 'Masquerade',
        onClick: () => this.router.transitionTo(EMBER_ROUTE_NAMES.ADMIN__MASQUERADE, user.id),
        hasSeparatorAfter: true,
      });
    }

    if (currentUserIsSiteAdmin || currentUserIsCompanyAdmin) {
      // reactivate
      if (user.companyMembership?.isDeactivated) {
        menuOptions.push({
          title: 'Reactivate Member',
          onClick: () => this.reactivateCompanyMembership.perform(user),
        });
      } else {
        // make admin & revoke admin
        if (user.companyMembership?.admin) {
          const allAdmins = this.args.allAdmins ?? [];
          if (allAdmins.length > 1) {
            menuOptions.push({
              title: 'Revoke Admin',
              onClick: () => this.revokeUserCompanyAdmin.perform(user, COMPANY_USER_ROLES.ADMIN),
            });
          }
        } else {
          menuOptions.push({
            title: 'Make Admin',
            onClick: () => this.makeUserCompanyAdmin.perform(user, COMPANY_USER_ROLES.ADMIN),
          });
        }

        // deactivate
        if (currentUser.id !== user.id) {
          menuOptions.push({
            title: 'Deactivate Member',
            onClick: () => this.deactivateCompanyMembership.perform(user),
          });
        }
      }
    }

    return menuOptions;
  }

  get isUpdatingTeamMembership() {
    return this.addCompanyTeamMembership.isRunning
      || this.addUserToAllTeams.isRunning
      || this.removeCompanyTeamMembership.isRunning
      || this.makeUserCompanyAdmin.isRunning
      || this.revokeUserCompanyAdmin.isRunning
      || this.reactivateCompanyMembership.isRunning
      || this.deactivateCompanyMembership.isRunning;
  }

  get userIdToCompanyMembershipMap() {
    const members = this.args.company?.companyMemberships ?? [];
    return members.reduce((accum, companyMembership) => {
      accum[companyMembership.user.id] = companyMembership;
      return accum;
    }, {});
  }

  get canAddUserToTeam() {
    const { currentUser } = this.session;
    return !this.args.isCompanyAdmin
      && !this.args.teamTagDisabled
      && this.menuTeamOptions.length
      && (
        currentUser.isAdmin
        || currentUser.isCompanyAdmin
      );
  }

  get canRemoveUserFromTeam() {
    const { currentUser } = this.session;
    return currentUser.isAdmin || currentUser.isCompanyAdmin;
  }

  @task
  *makeUserCompanyAdmin(user, role) {
    try {
      yield this.userCommandService.addCompanyRole(user, role);
      yield this.userCommandService.getUser(user.id, COMPANY_PROFILE__USER_TEAM_MEMBERSHIP_INCLUDES);
      this.applicationStateManager.addToast(`${user.fullNameOrEmail} is now an admin`);
    } catch (e) {
      handleErrorResponseWithModal(this, e, `Could not make ${user.fullNameOrEmail} an admin`);
    }
  }

  @task
  *revokeUserCompanyAdmin(user, role) {
    try {
      yield this.userCommandService.removeCompanyRole(user, role);
      yield this.userCommandService.getUser(user.id, COMPANY_PROFILE__USER_TEAM_MEMBERSHIP_INCLUDES);
      this.applicationStateManager.addToast(`${user.fullNameOrEmail} is no longer an admin`);
    } catch (e) {
      handleErrorResponseWithModal(this, e, `Could not revoke ${user.fullNameOrEmail} from being an admin`);
    }
  }

  @task
  *addCompanyTeamMembership(team, user) {
    try {
      yield this.companyTeamMembershipCommandService.storeCompanyTeamMembership(team.id, user.id);
      yield this.userCommandService.getUser(user.id, COMPANY_PROFILE__USER_TEAM_MEMBERSHIP_INCLUDES);
      this.applicationStateManager.addToast(`${user.fullNameOrEmail} has joined the "${team.name}" team`);
    } catch (e) {
      handleErrorResponseWithModal(this, e, `Could not add ${user.fullNameOrEmail} to the "${team.name}" team`);
    }
  }

  @task
  *addUserToAllTeams(user, company) {
    try {
      yield this.companyTeamMembershipCommandService.storeUserAsMemberOfAllTeams(company.id, user.id);
      yield this.userCommandService.getUser(user.id, COMPANY_PROFILE__USER_TEAM_MEMBERSHIP_INCLUDES);
      this.applicationStateManager.addToast(`${user.fullNameOrEmail} has joined the every team`);
    } catch (e) {
      handleErrorResponseWithModal(this, e, `Could not add ${user.fullNameOrEmail} to every team`);
    }
  }

  @task
  *removeCompanyTeamMembership(team, user) {
    try {
      yield this.companyTeamMembershipCommandService.destroyCompanyTeamMembership(team.id, user.id);
      yield this.userCommandService.getUser(user.id, COMPANY_PROFILE__USER_TEAM_MEMBERSHIP_INCLUDES);
      this.applicationStateManager.addToast(`${user.fullNameOrEmail} has been removed from the "${team.name}" team`);
    } catch (e) {
      handleErrorResponseWithModal(this, e, `Could not remove ${user.fullNameOrEmail} from the "${team.name}" team`);
    }
  }

  @task
  *reactivateCompanyMembership(user) {
    try {
      yield this.userCommandService.reactivateCompanyMembership(user);
      yield this.userCommandService.getUser(user.id, COMPANY_PROFILE__USER_TEAM_MEMBERSHIP_INCLUDES);
      this.applicationStateManager.addToast(`${user.fullNameOrEmail} has been reactivated`);
    } catch (e) {
      handleErrorResponseWithModal(this, e, `Could not reactivate ${user.fullNameOrEmail}`);
    }
  }

  @task
  *deactivateCompanyMembership(user) {
    try {
      yield this.userCommandService.deactivateCompanyMembership(user);
      yield this.userCommandService.getUser(user.id, COMPANY_PROFILE__USER_TEAM_MEMBERSHIP_INCLUDES);
      this.applicationStateManager.addToast(`${user.fullNameOrEmail} has been deactivated`);
    } catch (e) {
      handleErrorResponseWithModal(this, e, `Could not deactivate ${user.fullNameOrEmail}`);
    }
  }
}

export default CompanyMembers;
